import type { CSSProperties } from "react";
import { brand } from "./brand.config";

/**
 * The gate mark and wordmark, at documented proportions with enforced clear
 * space. The mark is never restyled, recoloured outside the palette, or
 * stretched — it inherits currentColor and its aspect ratio is fixed.
 */

type LogoVariant = "mark" | "lockup" | "wordmark";

interface LogoProps {
  variant?: LogoVariant;
  /** Mark height in pixels. Everything else scales from this. */
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/** Intrinsic aspect ratio of the mark, from brand-guidelines. */
const MARK_ASPECT = 496.801 / 258.066;

function Mark({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 496.801 258.066"
      width={size * MARK_ASPECT}
      height={size}
      role="img"
      aria-label={brand.identity.businessName}
      style={{ display: "block", flex: "0 0 auto" }}
    >
      <g fill="currentColor">
        <rect x="66.487" y="0" width="36.586" height="258.066" rx="18.293" />
        <rect x="393.728" y="0" width="36.586" height="258.066" rx="18.293" />
        <rect x="0" y="147.674" width="496.801" height="36.586" rx="7.931" />
        <rect x="113.920" y="110.472" width="268.961" height="23.782" rx="11.160" />
      </g>
    </svg>
  );
}

function Wordmark({ size }: { size: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        fontFamily: "var(--font-sans)",
        fontWeight: "var(--weight-regular)" as unknown as number,
        fontSize: size * 0.42,
        letterSpacing: brand.identity.logo.wordmarkTracking,
        // Letter-spacing adds trailing space; indent by the same amount to keep
        // the wordmark optically centred under the mark.
        textIndent: brand.identity.logo.wordmarkTracking,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {brand.identity.logo.wordmarkText}
    </span>
  );
}

export function Logo({ variant = "lockup", size = 32, className, style }: LogoProps) {
  // Clear space is part of the mark, not a layout accident.
  const pad = size * brand.identity.clearSpaceRatio;

  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: size * 0.28,
        padding: pad,
        color: "var(--accent)",
        ...style,
      }}
    >
      {variant !== "wordmark" && <Mark size={size} />}
      {variant !== "mark" && <Wordmark size={size} />}
    </span>
  );
}

export default Logo;
